import {isHandleDetail} from "./tds/parse3dsToHandle";
import {
    buildPacedDetailModulePayload,
    parsePacedDetailModuleDto,
} from "./pacedDetailModule";

/** Маркер файла сборки Module 3D. */
export const MODULE3D_ASSEMBLY_KIND = "module3d-assembly";
/** v2: карта position → поза. v1: полный снимок сцены (читаем как fallback). */
export const MODULE3D_ASSEMBLY_FILE_VERSION = 2;

const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const sanitizeFileName = (name) => {
    let base = String(name || "module-3d-assembly").trim();
    base = base.replace(/\.upme-module3d\.json$/i, "").replace(/\.json$/i, "");
    base = base
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    return `${base || "module-3d-assembly"}.upme-module3d.json`;
};

export const getCompositionPosition = (detail) => {
    const raw = detail?.position;
    if (raw == null) return null;
    const text = String(raw).trim();
    return text || null;
};

const isImportedLike = (inst) => Boolean(
    inst?.fromImport
    || inst?.detailData?.source === "dxf"
    || inst?.detailData?.source === "3ds"
    || inst?.detail?.source === "dxf"
    || inst?.detail?.source === "3ds"
    || isHandleDetail(inst?.detailData)
    || isHandleDetail(inst?.detail),
);

/** Поза сцены → тело, которое потом примет массовый метод (позиция → положение). */
export const poseDtoFromInstance = (inst) => buildPacedDetailModulePayload({
    localPosition: inst?.localPosition,
    axes: inst?.axes,
    faceFlip: Boolean(inst?.faceFlip),
    forCreate: true,
});

export const parseAssemblyPose = (value) => {
    if (!value || typeof value !== "object") return null;
    if (value.anchorPoint != null || value.axesX != null) {
        const parsed = parsePacedDetailModuleDto(value);
        if (!parsed?.localPosition || !parsed?.axes) return null;
        return parsed;
    }
    if (value.localPosition && value.axes) {
        return {
            localPosition: value.localPosition,
            axes: value.axes,
            faceFlip: Boolean(value.faceFlip),
        };
    }
    return null;
};

export const placementsFromPlacedDetails = (placedDetails = []) => {
    const placements = {};
    for (const inst of Array.isArray(placedDetails) ? placedDetails : []) {
        if (isImportedLike(inst)) continue;
        const position = getCompositionPosition(inst?.detail)
            || getCompositionPosition(inst?.detailData);
        if (!position || !inst?.localPosition || !inst?.axes) continue;
        placements[position] = poseDtoFromInstance(inst);
    }
    return placements;
};

const placementsFromRawMap = (rawMap) => {
    if (!rawMap || typeof rawMap !== "object" || Array.isArray(rawMap)) return {};
    const placements = {};
    Object.entries(rawMap).forEach(([position, value]) => {
        const key = String(position || "").trim();
        if (!key) return;
        const parsed = parseAssemblyPose(value);
        if (!parsed) return;
        placements[key] = poseDtoFromInstance(parsed);
    });
    return placements;
};

/**
 * Снимок сборки: ключ — position детали состава, значение — положение.
 */
export const buildModule3dAssemblyFile = ({
    placedDetails = [],
    moduleId = null,
    moduleName = null,
} = {}) => ({
    version: MODULE3D_ASSEMBLY_FILE_VERSION,
    kind: MODULE3D_ASSEMBLY_KIND,
    savedAt: Date.now(),
    moduleId: moduleId ?? null,
    moduleName: moduleName ?? null,
    placements: placementsFromPlacedDetails(placedDetails),
});

export const downloadModule3dAssemblyFile = (assembly, options = {}) => {
    const json = JSON.stringify(assembly, null, 2);
    const blob = new Blob([json], {type: "application/json"});
    const fileName = sanitizeFileName(
        options.fileName || assembly?.moduleName || `module-${assembly?.moduleId || "3d"}`,
    );
    downloadBlob(blob, fileName);
    return fileName;
};

export const parseModule3dAssemblyFile = (raw) => {
    if (!raw || typeof raw !== "object") {
        throw new Error("Некорректный файл сборки");
    }
    if (raw.kind != null && raw.kind !== MODULE3D_ASSEMBLY_KIND) {
        throw new Error("Это не файл сборки Module 3D");
    }
    const version = Number(raw.version);
    if (Number.isFinite(version) && version > MODULE3D_ASSEMBLY_FILE_VERSION) {
        throw new Error(
            `Файл новее, чем поддерживает клиент (v${version}). Обновите приложение.`,
        );
    }

    const fromMap = placementsFromRawMap(raw.placements);
    const fromLegacy = Object.keys(fromMap).length
        ? {}
        : placementsFromPlacedDetails(raw.placedDetails);
    const placements = Object.keys(fromMap).length ? fromMap : fromLegacy;

    if (!Object.keys(placements).length) {
        throw new Error("В файле нет положений деталей по позициям");
    }

    return {
        version: Number.isFinite(version) ? version : MODULE3D_ASSEMBLY_FILE_VERSION,
        kind: MODULE3D_ASSEMBLY_KIND,
        savedAt: raw.savedAt ?? null,
        moduleId: raw.moduleId ?? null,
        moduleName: raw.moduleName ?? null,
        placements,
    };
};

/**
 * Прочитать File → сборка с картой position → поза.
 * @param {File} file
 */
export const readModule3dAssemblyFile = async (file) => {
    if (!file) throw new Error("Файл не выбран");
    const name = String(file.name || "").toLowerCase();
    if (!name.endsWith(".json")) {
        throw new Error("Нужен файл .json / .upme-module3d.json");
    }
    let text;
    try {
        text = await file.text();
    } catch {
        throw new Error("Не удалось прочитать файл");
    }
    let raw;
    try {
        raw = JSON.parse(text);
    } catch {
        throw new Error("Файл не является корректным JSON");
    }
    return parseModule3dAssemblyFile(raw);
};
